import { useEffect } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { SessionProvider } from './state/session';
import { ToastProvider } from './state/toast';
import { ConsentProvider } from './state/consent';
import { Home } from './pages/Home';
import { Pricing } from './pages/Pricing';
import { Blog, BlogPost } from './pages/Blog';
import { Legal } from './pages/Legal';
import { Support } from './pages/Support';
import { Prepare } from './pages/Prepare';
import { Login, Register } from './pages/Auth';
import { Dashboard } from './pages/Dashboard';
import { NewEnvelope } from './pages/NewEnvelope';
import { EnvelopeDetail } from './pages/EnvelopeDetail';
import { Templates } from './pages/Templates';
import { Billing } from './pages/Billing';
import { Developers } from './pages/Developers';
import { Settings } from './pages/Settings';
import { Sign } from './pages/Sign';
import { Embed } from './pages/Embed';
import { Completed } from './pages/Completed';
import { Verify } from './pages/Verify';
import { Mailbox } from './pages/Mailbox';
import { NotFound } from './pages/NotFound';
import { syncClarity } from './lib/clarity';

/**
 * Starts Clarity on public marketing pages and stops it everywhere else.
 *
 * Mounted inside the router so it sees every navigation, including the one
 * that matters: a visitor who lands on a marketing page and then opens a
 * signing link or the dashboard in the same tab. The script survives that
 * transition; the recording must not.
 */
function ClarityScope() {
  const { pathname } = useLocation();
  useEffect(() => { syncClarity(pathname); }, [pathname]);
  return null;
}

export function App() {
  return (
    <BrowserRouter>
      <ConsentProvider>
      <ToastProvider>
        <SessionProvider>
          <ClarityScope />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/terms" element={<Legal />} />
            <Route path="/privacy" element={<Legal />} />
            <Route path="/support" element={<Support />} />
            {/* People guess both. */}
            <Route path="/contact" element={<Support />} />
            <Route path="/prepare" element={<Prepare />} />
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<Login />} />

            <Route path="/app" element={<Dashboard />} />
            <Route path="/app/new" element={<NewEnvelope />} />
            <Route path="/app/envelopes/:envelopeId" element={<EnvelopeDetail />} />
            <Route path="/app/templates" element={<Templates />} />
            <Route path="/app/billing" element={<Billing />} />
            <Route path="/app/developers" element={<Developers />} />
            <Route path="/app/settings" element={<Settings />} />

            {/* Recipient-facing routes need no account. */}
            <Route path="/sign/:token" element={<Sign />} />
            {/* Host applications frame this route with a single-use session. */}
            <Route path="/embed/:token" element={<Embed />} />
            <Route path="/completed/:token" element={<Completed />} />
            <Route path="/verify" element={<Verify />} />

            <Route path="/dev/mailbox" element={<Mailbox />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          {/* The analytics consent prompt is not mounted.
            *
            * There is nothing to consent to: this deployment collects no
            * analytics (ANALYTICS_ENABLED=0), so asking would be a dialogue
            * about a thing that does not happen.
            *
            * If measurement is ever switched back on, restore this. Loading
            * GA4 without the prompt is the one arrangement that would be
            * actually wrong -- `startAnalytics` is only ever called from a
            * granted consent state, so an unmounted banner means nothing is
            * ever granted and nothing is ever loaded. */}
        </SessionProvider>
      </ToastProvider>
      </ConsentProvider>
    </BrowserRouter>
  );
}
